import { typeIds, typeNames } from '~/config/settings/survey_element.settings';
import { iconBaseUrl } from '~/config/settings/worktango.settings';
import { datetime } from '~/tools/datetime';
import { pickLanguageCode } from '../languages';

/**
 * @module question_logic.transformer
 */

/**
 * Transform surveyElements from the server form to the front-end form.
 */
const surveyElementFrom = (surveyElement, supportedLanguageCodes) => {
  const { id, companyId, orderIndex } = surveyElement;

  const typeName = surveyElement.type.name;
  const typeKey = Object.keys(typeNames).find((key) => typeNames[key] === typeName);
  // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
  const typeId = typeIds[typeKey];

  const categoryId = surveyElement.question.category?.id;
  const isRequired = surveyElement.question.required;
  const allowComments = surveyElement.question.showTextComment;
  const showSkipButton = surveyElement.showSkipButton;
  const showLabels = surveyElement.question.showLabels;
  const showDescription = surveyElement.question.showDescription;
  const hideReporting = surveyElement.hideReporting;
  const isLibraryQuestion = surveyElement.isLibraryQuestion;
  const surveyTypeId = surveyElement.surveyTypeId;
  const isPreviousQuestion = surveyElement.isPreviousQuestion;
  const isBenchmarkedQuestion = surveyElement.isBenchmarkedQuestion;
  const isDiscontinued = surveyElement.isDiscontinued;
  const discontinuedDateUTC = surveyElement.discontinuedDateUTC;
  const minOptions = typeName === typeNames.YES_NO ? 1 : surveyElement.question.ratingOptionLowest;
  const maxOptions = typeName === typeNames.YES_NO ? 2 : surveyElement.question.ratingOptionHighest;

  // quesitonId refer to one of the following based on question type:
  // surveyElementQuestionScaleId or
  // surveyElementQuesitonOptionsId or
  // surveyElementQuestionTextId or
  // surveyElementQuestionMessageId
  const questionId = surveyElement.question.id;

  const minSelectedOption = surveyElement.question.minSelectedOption;
  const maxSelectedOption = surveyElement.question.maxSelectedOption;
  const allowMultipleSelect = minSelectedOption > 1 || maxSelectedOption > 1;
  // stand for how many mc options have been set
  const totalOptions = surveyElement.question.choices?.length;

  const sentTimes = surveyElement.sentTimes;
  const lastSent = surveyElement.lastSent
    ? datetime.format(new Date(Number(surveyElement.lastSent)), 'yyyy-MM-dd')
    : null;

  const isAttributeBased = surveyElement.isAttributeBased;
  const selectedAttributes =
    surveyElement.attributes &&
    surveyElement.attributes.reduce((selected, attribute) => {
      const values = selected[attribute.attributeId]
        ? [...selected[attribute.attributeId], attribute.attributeValue]
        : [attribute.attributeValue];

      return {
        ...selected,
        [attribute.attributeId]: values,
      };
    }, {});

  const iconId = surveyElement.question.icon?.id;
  const iconName = surveyElement.question.icon?.name;
  let iconUrl;
  switch (typeName) {
    case typeNames.YES_NO:
      iconUrl = `${iconBaseUrl}/yes_no/${iconName}`;
      break;
    case typeNames.ICON:
      iconUrl = `${iconBaseUrl}/ratings_fixed/ratings_${iconName}`;
      break;
    case typeNames.RATING:
    case typeNames.ENPS:
      iconUrl = `${iconBaseUrl}/ratings_sliding/ratings_${iconName}`;
      break;
    default:
      iconUrl = '';
      break;
  }

  const normalizedSupportedLanguageCodes = supportedLanguageCodes?.map((lc) => lc.toLowerCase());

  const isMessageType = typeName === typeNames.MESSAGE;
  const translations =
    surveyElement.question.messagesPerLanguage &&
    surveyElement.question.messagesPerLanguage.reduce((translations, messagePerLanguage) => {
      const languageId = Number(messagePerLanguage.language.id);
      const languageCode = messagePerLanguage.language.langISOCode;
      // Survey elements that were created from survey templates can sometimes have additional translations
      // That are not supported by the survey. Filter these out here, to make sure we only show correct titles
      if (
        supportedLanguageCodes?.length > 0 &&
        !normalizedSupportedLanguageCodes.includes(languageCode.toLowerCase())
      ) {
        return translations;
      }

      // note: for message type the message and desc is send in reverse order
      // this should be fixed by backend,
      // it related to survey app and reporting as well, keep it for now
      const title = isMessageType ? messagePerLanguage.description : messagePerLanguage.message;
      const desc = isMessageType ? messagePerLanguage.message : messagePerLanguage.description;

      const translation = {
        languageId,
        title,
        desc,
        minLabel: messagePerLanguage.labelBefore || '',
        maxLabel: messagePerLanguage.labelAfter || '',
      };
      surveyElement.question.choices &&
        surveyElement.question.choices.forEach((choice, index) => {
          const matchedMessage = choice.messagesPerLanguage.find((msg) => Number(msg.language.id) === languageId);
          if (matchedMessage) {
            translation[`option${index + 1}`] = matchedMessage.message;
          }
        });
      return {
        ...translations,
        [languageCode]: translation,
      };
    }, {});
  const pickedLanguage = pickLanguageCode(Object.keys(translations));
  const title = translations[pickedLanguage]?.title;

  const supportedLanguages = Object.keys(translations).join(', ');
  const optionChoices =
    surveyElement.question.choices &&
    surveyElement.question.choices.map((choice) => {
      return { id: choice.id, orderIndex: choice.orderIndex };
    });

  const result = {
    id,
    questionId,
    orderIndex,
    companyId,
    typeId,
    typeName,
    typeKey,
    categoryId,
    isRequired,
    allowComments,
    showSkipButton,
    showLabels,
    surveyTypeId,
    showDescription,
    hideReporting,
    isLibraryQuestion,
    isPreviousQuestion,
    isBenchmarkedQuestion,
    isAttributeBased,
    isDiscontinued,
    discontinuedDateUTC,
    selectedAttributes,
    minOptions,
    maxOptions,
    minSelectedOption,
    maxSelectedOption,
    title,
    translations,
    iconId,
    iconName,
    iconUrl,
    allowMultipleSelect,
    totalOptions,
    optionChoices,
    supportedLanguages,
    sentTimes,
    lastSent,
  };

  return result;
};

const surveyElementsFrom = (surveyElements, supportedLanguageCodes) => {
  const results = surveyElements
    ? surveyElements.map((surveyElement) => surveyElementFrom(surveyElement, supportedLanguageCodes))
    : [];

  return results;
};

export { surveyElementFrom, surveyElementsFrom };
